
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import AdPanel from "@/components/ads/AdPanel";
import DiscoveryContent from "@/components/discovery/DiscoveryContent";
import LocationPermissionDialog from "@/components/discovery/LocationPermissionDialog";

import RightSideMenu from "@/components/navigation/RightSideMenu";
import { AlphaLoader } from "@/components/ui/alpha-loader";
import { useAuth } from "@/hooks/useAuth";
import { useDiscoveryFilters } from "@/hooks/useDiscoveryFilters";
import { useProfileCache } from "@/hooks/useProfileCache";
import { useSubscriptionStatus } from "@/hooks/useSubscriptionStatus";
import { useGeolocation } from "@/hooks/useGeolocation";
import { useLocationPermissionPopup } from "@/hooks/useLocationPermissionPopup";
import { useLanguage } from "@/hooks/useLanguage";
import { useProfileViews } from "@/hooks/useProfileViews";
import { DiscoveryRibbonType } from "@/components/discovery/DiscoveryRibbons";
import { profileRouterService } from "@/services/profileRouterService";
import { UserActivityService } from "@/services/userActivityService";
import { useEffect } from "react";

const Discovery = () => {
  const { user } = useAuth();
  const { t, isRTL } = useLanguage();
  const navigate = useNavigate();
  const { showFilters, setShowFilters, filters, setFilters, hasActiveFilters } = useDiscoveryFilters();
  const { userProfile, profileLimit } = useSubscriptionStatus();
  const { location, permission } = useGeolocation();
  const { handleProfileView } = useProfileViews();
  const [activeRibbon, setActiveRibbon] = useState<DiscoveryRibbonType>("discovery");
  
  // Use the new subscription tier system - default to alpha200
  const currentTier = userProfile?.subscription_tier || 'alpha200';
  
  console.log('🎯 Discovery page state:', {
    activeRibbon,
    currentTier,
    profileLimit,
    userHasProfile: !!userProfile
  });
  
  const { nearbyUsers, isLoading, handleRefresh } = useProfileCache(filters, profileLimit, currentTier, activeRibbon);

  const [showLimitModal, setShowLimitModal] = useState(false);

  // Location permission popup hook
  const {
    showPopup: showLocationPopup,
    handleEnableLocation,
    handleNotNow,
    handleDontAskAgain
  } = useLocationPermissionPopup();

  // Track user activity when they visit the discovery page
  useEffect(() => {
    if (user?.id) {
      UserActivityService.updateActivityOnAction(user.id);
    }
  }, [user?.id]);

  const handleProfileViewAndNavigate = (userId: string, userProfile?: any) => {
    console.log('Profile viewed:', userId);
    
    // Track user activity when they view profiles
    if (user?.id) {
      UserActivityService.updateActivityOnAction(user.id);
    }
    
    // Track the profile view
    handleProfileView(userId);
    
    // Determine which profile route to use
    const profileRoute = profileRouterService.getProfileRoute(
      userId, 
      userProfile?.username || userProfile?.first_name, 
      userProfile?.display_name
    );
    
    // Navigate to the appropriate profile page
    navigate(profileRoute);
  };

  // Check if user has hit profile limit - Alpha200 users can still hit limits
  const hasHitLimit = nearbyUsers && nearbyUsers.length >= profileLimit && currentTier === 'alpha200';
  const upgradeMessage = `You've explored ${nearbyUsers?.length || 0} profiles today. Upgrade your Alpha status to see more connections!`;

  if (isLoading) {
    return (
      <div className={`min-h-screen bg-background text-foreground ${isRTL ? 'rtl' : 'ltr'}`}>
        <RightSideMenu />
        <AdPanel />
        <div className="flex items-center justify-center" style={{ height: 'calc(100vh - calc(100vh / 7))' }}>
          <AlphaLoader size="large" />
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-background text-foreground ${isRTL ? 'rtl' : 'ltr'}`}>
      
      <RightSideMenu />
      <AdPanel />
      
      <div className="container mx-auto px-4 py-2 max-w-6xl">
        <DiscoveryContent
          showFilters={showFilters}
          setShowFilters={setShowFilters}
          hasActiveFilters={hasActiveFilters}
          filters={filters}
          setFilters={setFilters}
          hasHitLimit={hasHitLimit}
          upgradeMessage={upgradeMessage}
          showLimitModal={showLimitModal}
          setShowLimitModal={setShowLimitModal}
          currentTier={currentTier}
          profileLimit={profileLimit}
          nearbyUsers={nearbyUsers || []}
          isLoading={isLoading}
          onRefresh={handleRefresh}
          onProfileView={handleProfileViewAndNavigate}
          hasLocation={!!location}
          locationPermission={permission}
          activeRibbon={activeRibbon}
          onRibbonChange={setActiveRibbon}
        />
      </div>

      <LocationPermissionDialog
        isOpen={showLocationPopup}
        onEnableLocation={handleEnableLocation}
        onNotNow={handleNotNow}
        onDontAskAgain={handleDontAskAgain}
      />
    </div>
  );
};

export default Discovery;
